import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import type { PricingTier } from '../types';

const PricingSection = () => {
  const pricingTiers: PricingTier[] = [
    {
      id: "1",
      name: "Starter",
      description: "Perfect for small businesses getting started with AI automation",
      price: "$299/mo",
      features: [
        "Up to 1,000 conversations/month",
        "Single channel integration",
        "Basic AI training",
        "Email support",
        "Standard analytics dashboard",
        "Knowledge base integration"
      ],
      highlighted: false,
      cta: "Start Free Trial"
    },
    {
      id: "2",
      name: "Professional",
      description: "For growing businesses scaling customer engagement",
      price: "$799/mo",
      features: [
        "Up to 5,000 conversations/month",
        "Multi-channel integration",
        "Advanced AI training",
        "Priority support",
        "Advanced analytics & insights",
        "CRM integration",
        "Custom workflows",
        "A/B testing capabilities"
      ],
      highlighted: true,
      cta: "Book Free Audit"
    },
    {
      id: "3",
      name: "Enterprise",
      description: "Custom solutions for large-scale operations",
      price: "Custom",
      features: [
        "Unlimited conversations",
        "All channel integrations",
        "Custom AI model training",
        "Dedicated account manager",
        "White-label options",
        "Custom integrations",
        "SLA guarantees",
        "Advanced security features"
      ],
      highlighted: false,
      cta: "Contact Sales"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="DollarSign" size={16} />
            <span>Transparent Pricing</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Choose Your Perfect Plan
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Flexible pricing that scales with your business. All plans include 14-day free trial
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              className={`bg-card rounded-2xl p-8 border-2 transition-all duration-fast hover:shadow-xl ${
                tier.highlighted
                  ? 'border-primary shadow-lg scale-105'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {tier.highlighted && (
                <div className="inline-flex items-center space-x-1 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold mb-4">
                  <Icon name="Star" size={12} />
                  <span>Most Popular</span>
                </div>
              )}
              
              <h3 className="text-2xl font-bold text-text-primary mb-2">
                {tier.name}
              </h3>
              
              <p className="text-text-secondary mb-6">
                {tier.description}
              </p>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-text-primary">
                  {tier.price}
                </span>
                {tier.price !== "Custom" && (
                  <span className="text-text-secondary ml-2">per month</span>
                )}
              </div>
              
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="Check" size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link to="/contact">
                <Button
                  variant={tier.highlighted ? "default" : "outline"}
                  size="lg"
                  fullWidth
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {tier.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <p className="text-text-secondary mb-4">
            All plans include free setup assistance and training
          </p>
          <Link to="/pricing" className="text-primary hover:text-primary/80 font-medium inline-flex items-center space-x-2">
            <span>View detailed pricing comparison</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;